import { RowContentData } from './types'

// Column width calculation using content-based sizing (matching PDF preview modal logic)
// Fixed columns: sized to content; Flexible columns: share remaining space proportionally

// Define which columns are fixed-width (sized to content) vs flexible
export const FIXED_WIDTH_COLUMNS = new Set([
  'serial', 'year', 'aftt', 'emp', 'damage', 'pax', 'paintInterior',
  'dateListed', 'datesSold', 'dateRemoved',
  'jnAsk', 'askingTake', 'pricesSold', 'externalPrices',
  // Custom columns are also fixed-width
  'custom_0', 'custom_1', 'custom_2'
])

export const FLEXIBLE_COLUMNS = new Set(['owner', 'broker', 'options', 'relativeValue'])

// Minimum column widths in pixels to prevent content from being squeezed
// These ensure columns maintain readable widths and trigger horizontal scroll when needed
export const COLUMN_MIN_WIDTHS: Record<string, number> = {
  'serial': 70,
  'owner': 100,
  'broker': 100,
  'year': 55,
  'aftt': 60,
  'emp': 55,
  'damage': 45,
  'pax': 40,
  'paintInterior': 85,
  'dateListed': 75,
  'datesSold': 70,
  'dateRemoved': 70,
  'jnAsk': 75,
  'askingTake': 80,
  'pricesSold': 85,
  'externalPrices': 80,
  'options': 160,        // Options need more space for option names with prices
  'relativeValue': 145,  // Relative value has breakdown with labels
  'custom_0': 70,
  'custom_1': 70,
  'custom_2': 70
}

// Character width factor - each character needs approximately this % of total width
// Based on ~7px per character at 1300px total width = 0.54%
// Adding small safety margin: 0.6% per character
export const CHAR_WIDTH_PCT = 0.6

// Per-column padding adjustments (some columns need less padding)
export const COLUMN_PADDING: Record<string, number> = {
  'serial': 1.5,      // S/N column - increased padding for better readability
  'year': 2.0,        // YOM/EIS - increased padding for better readability
  'aftt': 1.0,
  'emp': 0.8,         // Engine Prog - less padding since we break on words
  'damage': 1.5,      // DMG - extra padding for visual centering
  'pax': 1.0,
  'paintInterior': 0.6,
  'dateListed': 0.4,  // Date Listed - reduced padding
  'datesSold': 1.5,   // Listed/Sold - increased padding to prevent overflow
  'dateRemoved': 0.4, // Date Removed - reduced padding
  'jnAsk': 1.0,
  'askingTake': 1.0,
  'pricesSold': 1.5,  // Ask/Sold prices - extra padding for dollar amounts
  'externalPrices': 1.0,
  'custom_0': 0.6,    // Custom columns - less padding
  'custom_1': 0.6,
  'custom_2': 0.6
}

// Columns where we should measure by longest WORD (will wrap on spaces)
export const MEASURE_BY_WORD = new Set(['emp', 'custom_0', 'custom_1', 'custom_2'])

// Maximum characters before forcing text wrap for custom columns
export const CUSTOM_COLUMN_MAX_CHARS = 20

// Header text for each column (for measurement)
export const COLUMN_HEADERS: Record<string, string> = {
  'serial': 'S/N\nReg',
  'owner': 'Aircraft\nOwner',
  'broker': 'Aircraft\nAgent',
  'year': 'YOM\nEIS',
  'aftt': 'AFTT\nS/New',
  'emp': 'Engine\nProg',
  'damage': 'DMG',
  'pax': 'PAX',
  'paintInterior': 'Paint\nInterior',
  'options': 'Options &\nAdditional Equipment',
  'dateListed': 'Date Listed\nDOM',
  'jnAsk': 'JETNET\nAsk Price',
  'askingTake': 'Ask Price\nTake Price',
  'externalPrices': 'Adj. Ask\nAdj. Take',
  'datesSold': 'Listed\nSold',
  'pricesSold': 'Ask Price\nSold Price',
  'dateRemoved': 'Removed',
  'relativeValue': 'Relative Value',
  'custom_0': 'Custom',
  'custom_1': 'Custom',
  'custom_2': 'Custom'
}

export function calculateContentBasedColumnWidths(
  visibleColumns: string[],
  rowData: RowContentData[],
  customColumnTitles: Map<number, string>
): Map<string, number> {
  // Helper to measure the longest string content in a column
  const measureColumnContent = (dataKey: string): number => {
    // Get header text - measure only the LONGEST LINE (headers are multi-line by design)
    let headerText = COLUMN_HEADERS[dataKey] || dataKey

    // Use custom column title if available
    if (dataKey.startsWith('custom_')) {
      const customIndex = parseInt(dataKey.split('_')[1], 10)
      const customTitle = customColumnTitles.get(customIndex)
      if (customTitle) {
        headerText = customTitle
      }
    }

    let maxLength: number

    // For columns that wrap by word (including custom columns), measure header by longest word
    if (MEASURE_BY_WORD.has(dataKey)) {
      // Split header by both newlines and spaces to find longest word
      const headerWords = headerText.split(/[\n\s]+/)
      maxLength = Math.max(...headerWords.map(word => word.length))
    } else {
      // Standard: measure by longest line
      const headerLines = headerText.split('\n')
      maxLength = Math.max(...headerLines.map(line => line.length))
    }

    // Measure all row values for this column
    rowData.forEach(row => {
      const value = row[dataKey]
      if (typeof value === 'string') {
        // Handle multi-line values - find the longest line
        const lines = value.split('\n')

        if (MEASURE_BY_WORD.has(dataKey)) {
          // For word-wrap columns, measure the longest WORD
          lines.forEach(line => {
            const words = line.split(/\s+/)
            words.forEach(word => {
              maxLength = Math.max(maxLength, word.length)
            })
          })
        } else {
          // Standard: measure the longest line
          const longestLine = Math.max(...lines.map(line => line.length))
          maxLength = Math.max(maxLength, longestLine)
        }
      }
    })

    return maxLength
  }

  // Calculate widths for fixed columns based on content
  const fixedWidths: Map<string, number> = new Map()
  let totalFixedWidth = 0

  visibleColumns.forEach(col => {
    if (FIXED_WIDTH_COLUMNS.has(col)) {
      const contentLength = measureColumnContent(col)
      const padding = COLUMN_PADDING[col] ?? 1.0
      // Calculate width: character count * char width + padding
      const calculatedWidth = (contentLength * CHAR_WIDTH_PCT) + padding

      fixedWidths.set(col, calculatedWidth)
      totalFixedWidth += calculatedWidth
    }
  })

  // Calculate remaining space for flexible columns (total is 100%)
  const totalWidth = 100
  const remainingWidth = Math.max(totalWidth - totalFixedWidth, 20) // Ensure at least 20% for flex columns

  // Count flexible columns that are enabled
  const enabledFlexColumns = visibleColumns.filter(col => FLEXIBLE_COLUMNS.has(col))
  const hasOwner = enabledFlexColumns.includes('owner')
  const hasBroker = enabledFlexColumns.includes('broker')
  const hasOptions = enabledFlexColumns.includes('options')
  const hasRelativeValue = enabledFlexColumns.includes('relativeValue')

  // Distribute remaining space:
  // - Owner and Broker get equal share (pair 1)
  // - Options and Relative Value get equal share (pair 2)
  // Options/RelativeValue get 1.5x the share of Owner/Broker since they need more space

  let ownerBrokerWidth = 0
  let optionsRelValueWidth = 0

  const pair1Count = (hasOwner ? 1 : 0) + (hasBroker ? 1 : 0)
  const pair2Count = (hasOptions ? 1 : 0) + (hasRelativeValue ? 1 : 0)

  if (pair1Count > 0 || pair2Count > 0) {
    // Weight: pair1 gets 1 unit each, pair2 gets 1.5 units each
    const totalUnits = pair1Count * 1 + pair2Count * 1.5
    const unitWidth = remainingWidth / totalUnits

    ownerBrokerWidth = unitWidth * 1
    optionsRelValueWidth = unitWidth * 1.5
  }

  // Build final widths map
  const unnormalizedWidths: Map<string, number> = new Map()

  visibleColumns.forEach(col => {
    if (fixedWidths.has(col)) {
      unnormalizedWidths.set(col, fixedWidths.get(col)!)
    } else if (col === 'owner' || col === 'broker') {
      unnormalizedWidths.set(col, ownerBrokerWidth)
    } else if (col === 'options' || col === 'relativeValue') {
      unnormalizedWidths.set(col, optionsRelValueWidth)
    } else {
      // Fallback for any other column (shouldn't happen)
      unnormalizedWidths.set(col, 5)
    }
  })

  // Normalize to ensure total is exactly 100%
  const currentTotal = Array.from(unnormalizedWidths.values()).reduce((sum, w) => sum + w, 0)
  const widths = new Map<string, number>()
  unnormalizedWidths.forEach((width, key) => {
    widths.set(key, (width / currentTotal) * 100)
  })

  return widths
}

// Format damage history value for display
export const formatDamageHistory = (value: string | null): string => {
  if (!value) return '-'
  switch (value) {
    // API format
    case 'Y': return 'YES'
    case 'N': return 'NO'
    case 'U': return 'UNK'
    // Database format (from overrides)
    case 'Yes': return 'YES'
    case 'No': return 'NO'
    case 'Unknown': return 'UNK'
    default: return value
  }
}

